import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CampaignCode } from '@/types';
import { getCampaignTypeLabel, isCampaignValid } from '@/lib/campaigns';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Plus, Edit, Trash2, Copy, Check, X, Gift, Calendar, Users } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const AdminCampaigns = () => {
  const [campaigns, setCampaigns] = useState<CampaignCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignCode | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  
  // フォームデータ
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    campaignType: 'free_1month' as CampaignCode['campaignType'],
    discountAmount: 490,
    maxUses: undefined as number | undefined,
    validFrom: '',
    validUntil: '',
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const campaignsRef = collection(db, 'campaignCodes');
      const q = query(
        campaignsRef,
        where('status', '!=', 'deleted'),
        orderBy('status'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const codes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as CampaignCode[];
      
      setCampaigns(codes);
    } catch (error) {
      console.error('Failed to fetch campaign codes:', error);
      alert('キャンペーンコードの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  const handleOpenModal = (campaign?: CampaignCode) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        code: campaign.code,
        name: campaign.name,
        description: campaign.description || '',
        campaignType: campaign.campaignType,
        discountAmount: campaign.discountAmount || 490,
        maxUses: campaign.maxUses,
        validFrom: campaign.validFrom ? format(campaign.validFrom.toDate(), 'yyyy-MM-dd') : '',
        validUntil: campaign.validUntil ? format(campaign.validUntil.toDate(), 'yyyy-MM-dd') : '',
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        campaignType: 'free_1month',
        discountAmount: 490,
        maxUses: undefined,
        validFrom: '',
        validUntil: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCampaign(null);
  };

  const checkCodeExists = async (code: string, excludeId?: string): Promise<boolean> => {
    try {
      const campaignsRef = collection(db, 'campaignCodes');
      const q = query(campaignsRef, where('code', '==', code));
      const snapshot = await getDocs(q);
      
      // 編集中のキャンペーン自身は除外
      return snapshot.docs.some(doc => doc.id !== excludeId);
    } catch (error) {
      console.error('Failed to check code:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.code.trim() || !formData.name.trim()) {
      alert('コードとキャンペーン名を入力してください。');
      return;
    }

    // 英数字のみ許可
    if (!/^[a-zA-Z0-9]+$/.test(formData.code)) {
      alert('コードは英数字のみ使用できます。');
      return;
    }

    setGenerating(true);
    
    try {
      // 重複チェック
      const exists = await checkCodeExists(formData.code.toUpperCase(), editingCampaign?.id);
      if (exists) {
        alert('このコードは既に使用されています。別のコードを入力してください。');
        setGenerating(false);
        return;
      }

      // Build campaign data object without undefined values
      const campaignData: Record<string, any> = {
        code: formData.code.toUpperCase(),
        name: formData.name,
        description: formData.description,
        campaignType: formData.campaignType,
        updatedAt: Timestamp.now(),
      };
      
      // Only add optional fields if they have values
      if (formData.campaignType === 'fixed_price') {
        campaignData.discountAmount = formData.discountAmount;
      }
      if (formData.maxUses !== undefined) {
        campaignData.maxUses = formData.maxUses;
      }
      if (formData.validFrom) {
        campaignData.validFrom = Timestamp.fromDate(new Date(formData.validFrom));
      }
      if (formData.validUntil) {
        campaignData.validUntil = Timestamp.fromDate(new Date(formData.validUntil));
      }

      if (editingCampaign) {
        // 更新
        const campaignRef = doc(db, 'campaignCodes', editingCampaign.id);
        await updateDoc(campaignRef, campaignData);
        alert('キャンペーンコードを更新しました。');
      } else {
        // 新規追加
        await addDoc(collection(db, 'campaignCodes'), {
          ...campaignData,
          currentUses: 0,
          status: 'active',
          createdAt: Timestamp.now(),
        });
        alert(`キャンペーンコードを生成しました: ${formData.code.toUpperCase()}`);
      }
      
      handleCloseModal();
      fetchCampaigns();
    } catch (error) {
      console.error('Failed to save campaign code:', error);
      alert('キャンペーンコードの保存に失敗しました。');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateRandom = () => {
    const randomCode = generateRandomCode();
    setFormData({ ...formData, code: randomCode });
  };

  const handleDelete = async (campaignId: string) => {
    if (!confirm('このキャンペーンコードを削除してもよろしいですか？')) {
      return;
    }

    try {
      const campaignRef = doc(db, 'campaignCodes', campaignId);
      // 論理削除
      await updateDoc(campaignRef, {
        status: 'deleted',
        updatedAt: Timestamp.now(),
      });
      alert('キャンペーンコードを削除しました。');
      fetchCampaigns();
    } catch (error) {
      console.error('Failed to delete campaign code:', error);
      alert('キャンペーンコードの削除に失敗しました。');
    }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
      alert('コードのコピーに失敗しました。');
    }
  };

  const stats = {
    total: campaigns.length,
    active: campaigns.filter((c) => isCampaignValid(c)).length,
    expired: campaigns.filter((c) => c.status === 'expired' || !isCampaignValid(c)).length,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ページヘッダー */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">キャンペーンコード管理</h1>
            <p className="text-gray-600 mt-1">キャンペーンコードの生成と利用状況確認</p>
          </div>
          
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            新規作成
          </button>
        </div>

        {/* 統計カード */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Gift className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">総キャンペーン数</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">有効なキャンペーン</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.active}</p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-gray-600">期限切れ</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.expired}</p>
          </div>
        </div>

        {/* キャンペーンコードリスト */}
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">キャンペーンコード一覧</h2>
          
          {campaigns.length === 0 ? (
            <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <Gift className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">キャンペーンコードはまだありません</p>
                <p className="text-sm text-gray-400 mt-1">
                  「新規作成」ボタンからキャンペーンコードを作成できます
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">コード</th>
                    <th scope="col" className="px-6 py-3">キャンペーン名</th>
                    <th scope="col" className="px-6 py-3">内容</th>
                    <th scope="col" className="px-6 py-3">利用状況</th>
                    <th scope="col" className="px-6 py-3">有効期間</th>
                    <th scope="col" className="px-6 py-3">ステータス</th>
                    <th scope="col" className="px-6 py-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => {
                    const isValid = isCampaignValid(campaign);
                    
                    return (
                      <tr key={campaign.id} className="bg-white border-b hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-sm font-mono bg-gray-100 px-3 py-1 rounded font-bold">
                            {campaign.code}
                          </code>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                          {campaign.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {getCampaignTypeLabel(campaign.campaignType)}
                          {campaign.campaignType === 'fixed_price' && campaign.discountAmount && (
                            <span className="ml-1">（¥{campaign.discountAmount.toLocaleString()}/月）</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {campaign.currentUses}
                          {campaign.maxUses !== undefined && (
                            <span className="text-gray-500"> / {campaign.maxUses}</span>
                          )}
                          {campaign.maxUses === undefined && (
                            <span className="text-gray-500"> / 無制限</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                          {campaign.validFrom ? (
                            <div>{format(campaign.validFrom.toDate(), 'yyyy/MM/dd', { locale: ja })}</div>
                          ) : (
                            <div className="text-gray-400">即時</div>
                          )}
                          <div className="text-gray-400">〜</div>
                          {campaign.validUntil ? (
                            <div>{format(campaign.validUntil.toDate(), 'yyyy/MM/dd', { locale: ja })}</div>
                          ) : (
                            <div className="text-gray-400">無期限</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              isValid
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {isValid ? '有効' : '無効'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {isValid && (
                              <button
                                onClick={() => handleCopyCode(campaign.code)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                              >
                                {copiedCode === campaign.code ? (
                                  <>
                                    <Check className="w-3 h-3" />
                                    コピー済み
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3" />
                                    コピー
                                  </>
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleOpenModal(campaign)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(campaign.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* モーダル */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingCampaign ? 'キャンペーンコードを編集' : '新規キャンペーンコード'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* コード */}
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    コード <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                    className="input w-full font-mono"
                    placeholder="例: SPRING2026"
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    英数字のみ使用できます（自動的に大文字に変換されます）
                  </p>
                </div>
                
                <button
                  type="button"
                  onClick={handleGenerateRandom}
                  className="btn-secondary w-full text-sm"
                >
                  ランダム生成（8文字）
                </button>

                {/* キャンペーン名 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    キャンペーン名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input w-full"
                    placeholder="例: 春の新規登録キャンペーン"
                  />
                </div>

                {/* 説明 */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    説明（任意）
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="input w-full"
                    placeholder="キャンペーンの詳細説明..."
                  />
                </div>

                {/* キャンペーン内容 */}
                <div>
                  <label htmlFor="campaignType" className="block text-sm font-medium text-gray-700 mb-1">
                    キャンペーン内容 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="campaignType"
                    value={formData.campaignType}
                    onChange={(e) => setFormData({ ...formData, campaignType: e.target.value as CampaignCode['campaignType'] })}
                    className="input w-full"
                  >
                    <option value="free_1month">Standardプラン 1か月無料</option>
                    <option value="free_3months">Standardプラン 3ヵ月無料</option>
                    <option value="free_6months">Standardプラン 半年無料</option>
                    <option value="free_1year">Standardプラン 1年無料</option>
                    <option value="discount_50">永年 50% OFF</option>
                    <option value="fixed_price">永年固定金額</option>
                  </select>
                </div>

                {/* 固定金額（fixed_priceの場合のみ表示） */}
                {formData.campaignType === 'fixed_price' && (
                  <div>
                    <label htmlFor="discountAmount" className="block text-sm font-medium text-gray-700 mb-1">
                      月額料金（円） <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="discountAmount"
                      value={formData.discountAmount}
                      onChange={(e) => setFormData({ ...formData, discountAmount: parseInt(e.target.value) || 0 })}
                      required
                      min="0"
                      className="input w-full"
                      placeholder="例: 490"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      通常価格: ¥980/月
                    </p>
                  </div>
                )}

                {/* 利用回数制限 */}
                <div>
                  <label htmlFor="maxUses" className="block text-sm font-medium text-gray-700 mb-1">
                    最大利用回数（任意）
                  </label>
                  <input
                    type="number"
                    id="maxUses"
                    value={formData.maxUses || ''}
                    onChange={(e) => setFormData({ ...formData, maxUses: e.target.value ? parseInt(e.target.value) : undefined })}
                    min="1"
                    className="input w-full"
                    placeholder="未入力の場合は無制限"
                  />
                </div>

                {/* 有効期間 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="validFrom" className="block text-sm font-medium text-gray-700 mb-1">
                      有効期間開始日（任意）
                    </label>
                    <input
                      type="date"
                      id="validFrom"
                      value={formData.validFrom}
                      onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                      className="input w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      未入力の場合は即時有効
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700 mb-1">
                      有効期間終了日（任意）
                    </label>
                    <input
                      type="date"
                      id="validUntil"
                      value={formData.validUntil}
                      onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                      className="input w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      未入力の場合は無期限
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={generating}
                    className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generating ? '処理中...' : (editingCampaign ? '更新する' : '作成する')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-secondary flex-1"
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
